import { useGetNewAuthToken } from '@/app/digdashHook';
import { buildIFrameDigdashLink, type DigdashParams } from '@/components/common/iFrame.utils';
import type { IFrameLinkParams } from '@/models/common.type';
import { ActionIcon, Affix, Alert, Box, Center, Loader, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconMaximize,
  IconMaximizeOff,
} from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

const defaultStyle: CSSProperties = {
  position: 'absolute',
  top: '-1rem',
  left: '-1rem',
};

const fullBodyStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 101,
};

type Props = {
  link: string;
  type: 'iframe' | 'digdash';
  params?: IFrameLinkParams;
  digdashParams?: DigdashParams;
};

const IFramePage = ({ params, link, type, digdashParams }: Props) => {
  const { t } = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullBody, setIsFullBody] = useState(false);
  const {
    data: digdash,
    isLoading,
    error,
  } = useGetNewAuthToken({
    skip: type !== 'digdash',
  });
  const iframeLink =
    type === 'digdash' ? buildIFrameDigdashLink(link, digdash, digdashParams) : link;

  const iframeWidth = params?.width || '100vw';
  const iframeHeight =
    isFullscreen || isFullBody ? '100vh' : params?.height || 'calc(100vh - 120px)';

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    if (document.fullscreenElement === containerRef.current) {
      await document.exitFullscreen();
    } else {
      await containerRef.current.requestFullscreen();
    }
  };

  const toggleFullBody = () => {
    setIsFullBody(prev => !prev);
  };

  const style = isFullBody ? fullBodyStyle : defaultStyle;

  if (type === 'digdash') {
    if (isLoading) {
      return (
        <Center h="calc(100vh - 250px)" px="md">
          <Stack align="center" gap="xs">
            <Loader size="md" />
            <Text size="sm" c="dimmed">
              {t('iframe.digdash.loading')}
            </Text>
          </Stack>
        </Center>
      );
    }
    if (error) {
      return (
        <Center h="calc(100vh - 300px)" px="md">
          <Alert
            variant="light"
            color="red"
            radius="md"
            title={t('iframe.digdash.error.title')}
            icon={<IconAlertCircle size={18} />}
            maw={640}
            w="100%"
          >
            {t('iframe.digdash.error.description')}
          </Alert>
        </Center>
      );
    }
    if (!digdash) {
      return null;
    }
    console.log(iframeLink);
  }

  return (
    <Box ref={containerRef} pos="relative">
      <Affix position={{ bottom: 20, right: 20 }}>
        <Stack
          style={{
            zIndex: (fullBodyStyle.zIndex as number) + 10,
          }}
          gap="xs"
        >
          <Tooltip
            label={isFullBody ? t('common.exit_fullbody') : t('common.enter_fullbody')}
            position="left"
            withArrow
          >
            <ActionIcon
              onClick={toggleFullBody}
              aria-label={isFullBody ? t('common.exit_fullbody') : t('common.enter_fullbody')}
              variant="light"
            >
              {isFullBody ? <IconMaximizeOff /> : <IconMaximize />}
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={isFullscreen ? t('common.exit_fullscreen') : t('common.enter_fullscreen')}
            position="left"
            withArrow
          >
            <ActionIcon
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? t('common.exit_fullscreen') : t('common.enter_fullscreen')}
              variant="light"
            >
              {isFullscreen ? <IconArrowsMinimize /> : <IconArrowsMaximize />}
            </ActionIcon>
          </Tooltip>
        </Stack>
      </Affix>

      <iframe
        src={iframeLink}
        title={params?.title}
        allowFullScreen
        style={{
          border: 'none',
          display: 'block',
          ...style,
          width: iframeWidth,
          height: iframeHeight,
        }}
      />
    </Box>
  );
};

export default IFramePage;
